'use client';

/**
 * AgencyBrandingProvider — wraps the app and broadcasts agency branding.
 *
 * Reads /api/agency-branding once on mount + provides the values via React
 * context. Components that want to whitelabel read from the context hook.
 *
 * Also dispatches a custom 'agency-branding-changed' event so non-React
 * code (e.g. inline scripts) can react too.
 *
 * Why a context (and not just fetching everywhere): branding rarely
 * changes during a session, and we want all components to see a
 * consistent name/logo even after the user updates Settings without
 * reloading. The provider re-fetches on a tiny localStorage signal.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const DEFAULTS = {
  agencyName: 'Optilink AI Command Center',
  logoUrl: '',
  accentColor: '#2563eb',
  hideOriginalBranding: false,
  tagline: 'AI Command Center',
};

const Ctx = createContext({ branding: DEFAULTS, refresh: () => {} });

export function useAgencyBranding() {
  return useContext(Ctx);
}

export default function AgencyBrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);

  const refresh = useCallback(() => {
    fetch('/api/agency-branding')
      .then(r => r.json())
      .then(d => {
        if (d?.success && d.branding) {
          const b = { ...DEFAULTS, ...d.branding };
          // Old installs saved the previous default tagline; treat it as unset.
          if (!b.tagline || b.tagline === 'Enterprise Cold Engine') b.tagline = DEFAULTS.tagline;
          setBranding(b);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for a soft refresh signal — Settings page dispatches this after a save
  useEffect(() => {
    const h = () => refresh();
    window.addEventListener('agency-branding-changed', h);
    return () => window.removeEventListener('agency-branding-changed', h);
  }, [refresh]);

  return (
    <Ctx.Provider value={{ branding, refresh }}>
      {children}
    </Ctx.Provider>
  );
}
